/**
 * Feasibility preview (brief AUT-05, 5.5; design record Steps 1-2).
 *
 * Recomputes, WITHOUT fitting any model, everything the run would
 * face: the cells to be fitted (MET-01 enumeration), the areas per
 * cell, which cells will hit the under-ten warning or the
 * under-five refusal of the Section 9 estimate policy, which areas
 * have no direct estimate and will be named and excluded, and the
 * projected publication outcome per domain under the AUT-04
 * projected-CV formula. The gates mirror runAnalysis in
 * @sae/engine: candidate areas (respondents, covariate rows,
 * population counts), missing-direct handling (decision S4), the
 * Section 9.15 minimum-areas rule, and the engine's disclosure
 * rules including complementary suppression.
 *
 * Two engine constants are mirrored here because the engine's
 * package entry point does not re-export them (OPEN_QUESTIONS
 * Q27): the expert floor of five areas and the statewide sentinel.
 */
import { applyComplementarySuppression, applyDisclosureRules, computeDirectEstimates, normalQuantile, smoothDesignEffects, transformDomains, } from '@sae/engine';
import { normalizeAreaId, tableToRespondents } from '@sae/io';
import { applyCollapses, subgroupLevelsOf } from './collapse.js';
import { recodeIndicators } from './rules.js';
/** Mirrors MIN_AREAS_TO_FIT in the engine (fayherriot.ts): no fit under five areas. */
export const ENGINE_MIN_AREAS_TO_FIT = 5;
/** Mirrors STATEWIDE_AREA_ID in the engine (direct.ts). */
export const STATEWIDE_AREA_ID = '__statewide__';
const DEFAULT_DISCLOSURE = {
    preset: 'proportion-standard',
    minRespondents: 10,
    cvFlag: 0.3,
    cvSuppress: 0.5,
    minPsusPerArea: 1,
};
function blocked(reason, log) {
    return {
        ok: false,
        blockedReason: reason,
        log,
        totalCells: 0,
        fittedCells: 0,
        warningCells: 0,
        insufficientCells: 0,
        refusedCells: 0,
        candidateAreas: [],
        areasWithoutDirect: [],
        projectedSuppressedDomains: 0,
        cells: [],
    };
}
export function previewFeasibility(input) {
    const model = input.model ?? {};
    // Mirror of the shipped runAnalysis gate (decision S4, directive
    // C-7): the default minimum is five areas — five to nine fit with a
    // warning, below five the fit itself refuses.
    const minAreas = model.minAreas ?? 5;
    const confidence = model.confidence ?? 0.95;
    const disclosure = input.disclosure ?? DEFAULT_DISCLOSURE;
    const log = [];
    if (input.roles.areaId === undefined || input.roles.weight === undefined) {
        return blocked('an area variable and a weight variable are required', log);
    }
    if (input.indicatorRules.length === 0) {
        return blocked('at least one indicator rule is required', log);
    }
    // Recode indicators and apply collapsing, exactly as the run will.
    const recoded = recodeIndicators(input.table, input.indicatorRules);
    log.push(...recoded.log);
    let working = recoded.table;
    const collapses = input.collapses ?? [];
    if (collapses.length > 0) {
        const collapsed = applyCollapses(working, collapses);
        log.push(...collapsed.log);
        working = collapsed.table;
    }
    const collapseByVariable = new Map(collapses.map((c) => [c.variable, c]));
    const subgroupSpecs = input.roles.subgroups.map((variable) => {
        const rule = collapseByVariable.get(variable);
        if (rule !== undefined) {
            return { variable, levels: subgroupLevelsOf(rule) };
        }
        const seen = new Set();
        const idx = working.columns.findIndex((c) => c.name === variable);
        for (const row of working.rows) {
            const v = row[idx];
            if (v !== null && v !== undefined)
                seen.add(v);
        }
        return {
            variable,
            levels: [...seen].map((value) => ({ value })),
        };
    });
    let respondents;
    let psuPopulationCounts;
    try {
        const conversion = tableToRespondents(working, {
            area: input.roles.areaId,
            weight: input.roles.weight,
            ...(input.roles.stratum !== undefined
                ? { stratum: input.roles.stratum }
                : {}),
            ...(input.roles.psu !== undefined ? { psu: input.roles.psu } : {}),
            ...(input.roles.psuPopulationCount !== undefined
                ? { psuPopulation: input.roles.psuPopulationCount }
                : {}),
            indicators: input.indicatorRules.map((r) => r.sourceColumn),
            subgroups: input.roles.subgroups,
        });
        respondents = conversion.respondents;
        psuPopulationCounts = conversion.psuPopulationCounts;
    }
    catch (err) {
        return blocked(`the microdata did not convert: ${err instanceof Error ? err.message : String(err)}`, log);
    }
    if (respondents.length === 0) {
        return blocked('no usable respondents remain after conversion', log);
    }
    const indicatorIds = input.indicatorRules.map((r) => r.id);
    const direct = computeDirectEstimates(respondents, {
        indicators: indicatorIds,
        subgroups: subgroupSpecs,
        lonelyPsu: model.lonelyPsu ?? 'adjust',
        confidence,
        // Frame PSU counts collected at ingestion (Addendum 37): the
        // preview's design effects use the same corrected basis the
        // run will use, so the projection and the run cannot disagree
        // about the basis.
        ...(psuPopulationCounts !== undefined ? { psuPopulationCounts } : {}),
    });
    const deffs = smoothDesignEffects(direct, {
        method: model.deffMethod ?? 'state',
        ...(model.deffFixedValue !== undefined
            ? { fixedValue: model.deffFixedValue }
            : {}),
        min: model.deffMin ?? 1,
        max: model.deffMax ?? 5,
    });
    const transformed = transformDomains(direct, deffs, {
        transform: model.transform ?? 'arcsin',
    });
    const deffByCell = new Map(deffs.map((d) => [d.cellId, d.designEffect]));
    // Candidate areas, exactly as runAnalysis assembles them.
    const candidate = new Set();
    for (const r of respondents)
        candidate.add(r.areaId);
    for (const a of input.extraAreas ?? [])
        candidate.add(normalizeAreaId(a));
    for (const a of Object.keys(input.populationCounts ?? {})) {
        candidate.add(normalizeAreaId(a));
    }
    const candidateAreas = [...candidate].sort();
    const directByCell = new Map();
    for (const d of direct) {
        if (d.domain.areaId === STATEWIDE_AREA_ID)
            continue;
        let arr = directByCell.get(d.domain.cellId);
        if (!arr) {
            arr = [];
            directByCell.set(d.domain.cellId, arr);
        }
        arr.push(d);
    }
    const transformedByCell = new Map();
    for (const td of transformed) {
        if (td.domain.areaId === STATEWIDE_AREA_ID)
            continue;
        let arr = transformedByCell.get(td.domain.cellId);
        if (!arr) {
            arr = [];
            transformedByCell.set(td.domain.cellId, arr);
        }
        arr.push(td);
    }
    const statewideByCell = new Map();
    for (const d of direct) {
        if (d.domain.areaId === STATEWIDE_AREA_ID)
            statewideByCell.set(d.domain.cellId, d);
    }
    const cellKeys = [];
    for (const id of indicatorIds)
        cellKeys.push({ cellId: id, indicator: id });
    for (const spec of subgroupSpecs) {
        for (const level of spec.levels) {
            for (const id of indicatorIds) {
                cellKeys.push({
                    cellId: `${id}|${spec.variable}|${String(level.value)}`,
                    indicator: id,
                    variable: spec.variable,
                    level: level.value,
                });
            }
        }
    }
    // The engine's verified normalQuantile (Tier 2 Item 7
    // deduplication): this module previously carried a private
    // Acklam copy — a faithful transcription (it agreed with the
    // engine's distributions.ts Acklam to every printed digit and
    // with the canonical quantile to <= 6.7e-9), but a private copy
    // of a numerical routine is the C-1 defect class regardless of
    // its current correctness, so the copy is deleted.
    const z = normalQuantile(0.5 + confidence / 2);
    const areasWithoutDirectSet = new Set();
    const works = [];
    for (const key of cellKeys) {
        const cellDirect = directByCell.get(key.cellId) ?? [];
        const cellTransformed = transformedByCell.get(key.cellId) ?? [];
        const present = new Set(cellDirect.map((d) => d.domain.areaId));
        const missingAreas = candidateAreas.filter((a) => !present.has(a));
        for (const a of missingAreas)
            areasWithoutDirectSet.add(a);
        const fittable = cellTransformed.filter((td) => td.effectiveSampleSize > 0).length;
        let outcome;
        if (fittable < ENGINE_MIN_AREAS_TO_FIT)
            outcome = 'refused';
        else if (fittable < minAreas)
            outcome = 'insufficient-areas';
        else if (fittable < 10)
            outcome = 'fitted-with-warning';
        else
            outcome = 'fitted';
        // Projected CV per domain (AUT-04). Domains without a direct
        // estimate are model-only in the run; they carry no projection.
        const statewide = statewideByCell.get(key.cellId);
        const pState = statewide?.estimate;
        const deff = deffByCell.get(key.cellId);
        const transformedByArea = new Map(cellTransformed.map((td) => [td.domain.areaId, td]));
        const inputs = cellDirect.map((d) => {
            const neff = transformedByArea.get(d.domain.areaId)?.effectiveSampleSize ?? 0;
            let projectedCv = null;
            if (pState !== undefined &&
                deff !== undefined &&
                pState > 0 &&
                pState < 1 &&
                d.n > 0) {
                projectedCv =
                    Math.sqrt((deff * pState * (1 - pState)) / d.n) / pState;
            }
            const width = projectedCv !== null && pState !== undefined
                ? 2 * z * pState * projectedCv
                : 0;
            return {
                domain: {
                    cellId: d.domain.cellId,
                    indicatorId: d.domain.indicatorId,
                    areaId: d.domain.areaId,
                    ...(d.domain.subgroupVariable !== undefined
                        ? {
                            subgroupVariable: d.domain.subgroupVariable,
                            subgroupLevel: d.domain.subgroupLevel,
                        }
                        : {}),
                },
                n: d.n,
                cv: projectedCv ?? Infinity,
                intervalWidth: width,
                relativeIntervalWidth: pState !== undefined && pState > 0
                    ? width / pState
                    : width > 0
                        ? Infinity
                        : 0,
                psuCount: d.psuCount,
                effectiveSampleSize: neff,
                degreesOfFreedom: d.degreesOfFreedom,
                // The domain's own event count is exact (not a projection),
                // so the standard preset's zero-event review flag projects
                // faithfully (Tier 2 Item 6). The Korn-Graubard width
                // fields are NOT projected: this preview's widths are the
                // documented statewide-proportion projection, not the
                // domain's own interval, and the run is the authority for
                // the width tests.
                eventCount: d.yesCount,
                projectedCv,
            };
        });
        works.push({ key, present, missingAreas, fittable, outcome, inputs });
    }
    const allInputs = works.flatMap((w) => w.inputs);
    const allOutcomes = applyComplementarySuppression(applyDisclosureRules(allInputs, disclosure), allInputs);
    const outcomeByDomain = new Map(allOutcomes.map((o) => [`${o.domain.cellId}|${o.domain.areaId}`, o]));
    // Pass 2: assemble the per-cell previews.
    const cells = [];
    let projectedSuppressedDomains = 0;
    for (const work of works) {
        const { key } = work;
        const inputByArea = new Map(work.inputs.map((i) => [i.domain.areaId, i]));
        const outcomeByArea = new Map(work.inputs.map((i) => [
            i.domain.areaId,
            outcomeByDomain.get(`${key.cellId}|${i.domain.areaId}`),
        ]));
        const domains = candidateAreas.map((areaId) => {
            const entry = inputByArea.get(areaId);
            if (entry === undefined) {
                return {
                    areaId,
                    n: 0,
                    effectiveSampleSize: 0,
                    projectedCv: null,
                    projectedStatus: 'no-direct-estimate',
                };
            }
            const o = outcomeByArea.get(areaId);
            const status = o?.status ?? 'publish';
            if (status === 'suppressed')
                projectedSuppressedDomains++;
            return {
                areaId,
                n: entry.n,
                effectiveSampleSize: entry.effectiveSampleSize,
                projectedCv: entry.projectedCv,
                projectedStatus: status,
                ...(o?.ruleId !== undefined ? { projectedRuleId: o.ruleId } : {}),
            };
        });
        cells.push({
            cellId: key.cellId,
            indicator: key.indicator,
            ...(key.variable !== undefined ? { subgroupVariable: key.variable } : {}),
            ...(key.level !== undefined ? { subgroupLevel: key.level } : {}),
            presentAreas: work.present.size,
            fittableAreas: work.fittable,
            missingDirectAreas: work.missingAreas,
            outcome: work.outcome,
            projectedPublish: domains.filter((d) => d.projectedStatus === 'publish').length,
            projectedFlagged: domains.filter((d) => d.projectedStatus === 'flagged').length,
            projectedSuppressed: domains.filter((d) => d.projectedStatus === 'suppressed').length,
            domains,
        });
    }
    return {
        ok: true,
        log,
        totalCells: cells.length,
        fittedCells: cells.filter((c) => c.outcome === 'fitted').length,
        warningCells: cells.filter((c) => c.outcome === 'fitted-with-warning').length,
        insufficientCells: cells.filter((c) => c.outcome === 'insufficient-areas').length,
        refusedCells: cells.filter((c) => c.outcome === 'refused').length,
        candidateAreas,
        areasWithoutDirect: [...areasWithoutDirectSet].sort(),
        projectedSuppressedDomains,
        cells,
    };
}
//# sourceMappingURL=feasibility.js.map